/* eslint-disable camelcase */
import { useCallback, useEffect, useMemo } from 'react';

import { useMutation, useManualQuery } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import { v4 as uuidv4 } from 'uuid';

import { TOKEN_TYPES } from '#client/constants/tokens';
import useBankingAccounts from '#client/hooks/useBankingAccounts';
import useConst from '#client/hooks/useConst';
import { useLogError } from '#client/hooks/useLogError';
import {
  CreatePayeesPlaidMutationGQL,
  RELINK_PLAID_MUTATION,
  UNLINK_PLAID_ACCOUNT_MUTATION
} from '#client/mutations';
import { GET_PLAID_LINK_TOKEN_QUERY, UPDATE_PLAID_LINK_TOKEN_QUERY } from '#client/queries';
import { retrievedGraphQLError } from '#client/utils/errors';
import {
  CREATE_PAYEES_PLAID_SPECIFIC_ERRORS,
  UNIT_ERROR_MESSAGES,
  createErrorObject
} from '#client/utils/Plaid/createErrorObject';

import useCustomerToken from './useCustomerToken';
import useRedirectToLinkedAccounts, { LINK_INTENT } from './useRedirectToLinkedAccounts';
import useToastContext from './useToastContext';
import isPlaidLinkSuccessful from '../utils/Plaid/isPlaidLinkSuccessful';
import { getPlaid, isPlaidInstalled, isPlaidEnabled } from '../utils/Plaid/PlaidService';

function manageDisplayPlaid(
  linkToken,
  { onPlaidLinkSuccess, onPlaidLinkError } = {
    onPlaidLinkSuccess: () => {},
    onPlaidLinkError: () => {}
  }
) {
  function plaidLinkCallback({ error = null, metadata, publicToken }) {
    const success = isPlaidLinkSuccessful(
      {
        error,
        metadata
      },
      onPlaidLinkError
    );

    if (success) {
      onPlaidLinkSuccess(publicToken, metadata);
    }
  }

  return getPlaid({
    plaidLinkCallback,
    linkToken
  });
}

/**
 *
 * Use Plaid hook
 *
 * @param {object} [options] Options for plaid UI
 * @param {string} [options.unitAccountId] unit account Id, required
 * @param {boolean} [options.isRelink] to trigger relink account flow
 * @param {string} [options.counterPartyId] counterparty id (use for relink flow)
 * @param {string} [options.businessEntityUuid] business entity uuid, required
 * @param {[string]} [options.counterPartyFields] counter party fields to be returned, optional,
 * by default object will contains only 'id' field
 * @param {Function} [options.onCounterPartyReceived] event onCounterPartyReceived(counterparty),
 * optional, it will receive counterparty as param
 * @param {Function} [options.onPlaidError] event onPlaidError(error, metadata)
 * optional, it will receive plaid error and metadata
 * @param {Function} [options.onCounterPartyCreatonError] event onCounterPartyCreatonError(error),
 * optional, it will receive graphQL error
 *
 * @returns {Array} where [isPlaidReady: boolean, plaid: { open(), exit(), destroy() }], plaid object described https://plaid.com/docs/link/web/#create
 */
function usePlaid({
  unitAccountId,
  businessEntityUuid,
  counterPartyFields,
  onCounterPartyReceived,
  onPlaidError,
  onCounterPartyCreatonError,
  isRelink,
  counterPartyId
}) {
  const handleCounterPartyReceived = onCounterPartyReceived || (() => {});
  const handlePlaidError = onPlaidError || (() => {});
  const handleCounterPartyCreatonError = onCounterPartyCreatonError || (() => {});
  const { createSuccessToast, createErrorToast } = useToastContext();
  const { redirectToLinkedAccounts } = useRedirectToLinkedAccounts(businessEntityUuid);
  const { id } = useBankingAccounts();
  const idempotencyKey = useConst(uuidv4);

  const { getCustomerToken, loading: isLoadingToken } = useCustomerToken({
    type: TOKEN_TYPES.COMMON
  });
  const captureException = useCaptureException();

  const CREATE_PAYEES_PLAID_MUTATION = CreatePayeesPlaidMutationGQL(counterPartyFields);
  const [
    createCounterParty,
    {
      loading: isCreatingCounterPartyLoading,
      errors: createCounterPartyErrors,
      error: createCounterPartyError
    }
  ] = useMutation(CREATE_PAYEES_PLAID_MUTATION, {
    noThrow: true
  });

  useLogError(createCounterPartyErrors, createCounterPartyError);
  const [
    relinkPlaidAccount,
    {
      loading: isRelinkAccountLoading,
      errors: relinkPlaidAccountErrors,
      error: relinkPlaidAccountError
    }
  ] = useMutation(RELINK_PLAID_MUTATION, {
    noThrow: true
  });

  useLogError(relinkPlaidAccountErrors, relinkPlaidAccountError);
  const [
    getPlaidLinkToken,
    {
      data: plaidTokenData,
      loading: isFetchingPlaidToken,
      errors: plaidTokenErrors,
      error: plaidTokenError
    }
  ] = useManualQuery(GET_PLAID_LINK_TOKEN_QUERY, {
    variables: {
      input: {
        businessEntityUuid
      }
    },
    skip: !unitAccountId || !businessEntityUuid,
    skipCache: true,
    noThrow: true
  });

  useLogError(plaidTokenErrors, plaidTokenError);
  const [
    updatePlaidLinkToken,
    {
      data: plaidTokenDataUpdated,
      loading: isUpdatingPlaidLinkToken,
      errors: plaidTokenErrorsUpdated,
      error: plaidTokenErrorUpdated
    }
  ] = useManualQuery(UPDATE_PLAID_LINK_TOKEN_QUERY, {
    variables: {
      input: {
        identifier: {
          businessUuid: businessEntityUuid,
          unitAccountId
        },
        counterpartyId: counterPartyId
      }
    },
    skip: !unitAccountId || !businessEntityUuid,
    skipCache: true,
    noThrow: true
  });

  useLogError(plaidTokenErrorsUpdated, plaidTokenErrorUpdated);
  const [unlinkPlaidAccount, { errors: unlinkPlaidAccountErrors, error: unlinkPlaidAccountError }] =
    useMutation(UNLINK_PLAID_ACCOUNT_MUTATION, {
      noThrow: true
    });
  useLogError(unlinkPlaidAccountErrors, unlinkPlaidAccountError);

  // If the relink flow, should get plaid token updated
  const plaidLinkToken = isRelink
    ? plaidTokenDataUpdated?.updatePlaidLinkToken?.token
    : plaidTokenData?.plaidLinkToken?.token;

  const isPlaidReady = isPlaidEnabled() && isPlaidInstalled() && Boolean(plaidLinkToken);

  // create a error toast
  const createErrorToastHandler = (title, description, skipToast, metadata, error) => {
    if (!skipToast) {
      createErrorToast({
        title,
        description: description || null
      });
    }
    handleCounterPartyCreatonError({
      title,
      description,
      metadata,
      error
    });
  };

  // create a success toast
  const createSuccessToastHandler = (metadata = {}, counterPartyReceived = {}) => {
    const { institution, account } = metadata;
    const accountSubtype = account?.subtype || '';
    const accountMask = account?.mask || '';
    const institutionName = institution?.name || 'Institution';
    const counterPartyReceivedBank = counterPartyReceived?.bank || 'Bank';

    const title = isRelink
      ? `${counterPartyReceivedBank} is relinked`
      : `${institutionName} ${
          accountSubtype === 'unknown' ? '' : accountSubtype
        } *${accountMask} is linked`;
    createSuccessToast({
      title,
      description: 'You can now make transactions with this bank account.'
    });
    handleCounterPartyReceived(counterPartyReceived);
  };

  // relink flow
  const relinkHandler = async (plaidPublicToken, metadata) => {
    const customerToken = await getCustomerToken();
    if (!customerToken) {
      return;
    }

    const { institution, accounts } = metadata;

    const institutionInput = {
      name: institution.name,
      institutionId: institution.institution_id
    };

    const accountsInput = accounts.map((account) => ({
      id: account.id,
      name: account.name,
      ...(account.mask && {
        mask: account.mask
      }),
      type: account.type,
      subtype: account.subtype
    }));

    const { data: relinkPlaidData, error } = await relinkPlaidAccount({
      variables: {
        input: {
          id: counterPartyId,
          partnerPublicToken: plaidPublicToken,
          customerToken,
          institution: institutionInput,
          accounts: accountsInput,
          bankingAccountId: id
        }
      }
    });

    const relinkCounterpartyStatus = relinkPlaidData?.relinkBankingPartnerCounterparty?.status;
    const counterPartyReceived =
      relinkPlaidData?.relinkBankingPartnerCounterparty?.bankingCounterparty;

    if (error) {
      createErrorToastHandler(
        'Your account could not be relinked',
        'There was an issue when relinking your account. Please try again.'
      );
    } else if (!relinkCounterpartyStatus) {
      createErrorToastHandler(
        'Your account could not be relinked',
        'There was an issue when relinking your account. Please try again.'
      );
    } else {
      createSuccessToastHandler(metadata, counterPartyReceived);
    }
  };

  const createCounterPartyErrorHandler = (error, metadata) => {
    // get message from error and check it is a specific error or not.
    const errorMessage = retrievedGraphQLError(error);
    const isSpecificError = CREATE_PAYEES_PLAID_SPECIFIC_ERRORS.includes(errorMessage);
    if (isSpecificError) {
      // get bank name
      const { institution, account } = metadata;
      const accountMask = account?.mask ? `(..${account.mask})` : '';
      const institutionName = institution?.name || 'Institution';

      const title = 'Your account has already been linked';
      const description = `${institutionName} ${accountMask} has already been linked. Please select your account in the list below.`;

      createErrorToastHandler(title, description);
    } else {
      const { title, description } = createErrorObject(errorMessage, true);

      const skipToast = errorMessage === UNIT_ERROR_MESSAGES.NAME_VALIDATION_FAILS.message;
      createErrorToastHandler(title, description, skipToast, metadata, error);
    }
  };

  // create new a counterparty
  const createCounterPartyHandler = async (plaidPublicToken, metadata) => {
    const { institution, accounts } = metadata;

    const institutionInput = {
      name: institution.name,
      institutionId: institution.institution_id
    };

    const accountsInput = accounts.map((account) => ({
      id: account.id,
      name: account.name,
      ...(account.mask && {
        mask: account.mask
      }),
      type: account.type,
      subtype: account.subtype
    }));

    const { data: createCounterPartyData, error } = await createCounterParty({
      variables: {
        input: {
          idempotencyKey,
          partnerPublicToken: plaidPublicToken,
          institution: institutionInput,
          accounts: accountsInput,
          bankingAccountId: id
        }
      }
    });

    const counterPartyStatus = createCounterPartyData?.createBankingPartnerCounterparty?.status;
    const counterPartyDataObject = createCounterPartyData?.createBankingPartnerCounterparty;

    if (error) {
      createCounterPartyErrorHandler(error, metadata);
    } else if (!(counterPartyStatus && counterPartyDataObject)) {
      createErrorToastHandler(
        'Error linking account',
        'There has been an error retrieving Plaid counterparty information'
      );
    } else {
      createSuccessToastHandler(metadata, counterPartyDataObject);
    }
  };

  /**
   * Create an Plaid object with three functions, open, exit, and destroy.
   * Calling open will display the Consent Pane view, calling exit will close Link,
   * and calling destroy will clean up the iframe.
   *
   * @returns {object} object with three functions, open, exit, and destroy
   */
  const plaid = useMemo(
    () =>
      isPlaidReady
        ? manageDisplayPlaid(plaidLinkToken, {
            onPlaidLinkError: async (error, metadata) => {
              // Extracting error code from the error object
              const { error_code } = error || {};

              // Retrieving customer token
              const customerToken = await getCustomerToken();

              // If there's no customer token or the error code is not 'INVALID_UPDATED_USERNAME'
              if (!customerToken || error_code !== 'INVALID_UPDATED_USERNAME') {
                // Call the function to handle Plaid error
                handlePlaidError(error, metadata);
                return;
              }

              // If the error code is 'INVALID_UPDATED_USERNAME', unlink the Plaid account
              const { error: unkLinkError } = await unlinkPlaidAccount({
                variables: {
                  input: {
                    customerToken,
                    id: counterPartyId,
                    reason: 'INVALID_UPDATED_USERNAME', // Providing reason for unlinking
                    bankingAccountId: id
                  }
                }
              });

              // If there's an error while unlinking the Plaid account
              if (unkLinkError) {
                captureException(error);
              }

              // Call the function to handle Plaid error
              handlePlaidError(error, metadata);
            },
            onPlaidLinkSuccess: async (plaidPublicToken, metadata) => {
              if (isRelink) {
                await relinkHandler(plaidPublicToken, metadata);
              } else {
                await createCounterPartyHandler(plaidPublicToken, metadata);
              }
            }
          })
        : {
            open: () => {},
            exit: () => {},
            destroy: () => {}
          },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [businessEntityUuid, plaidLinkToken, isPlaidReady, unitAccountId]
  );

  useEffect(() => {
    const fetchedPlaidToken = !isFetchingPlaidToken && !!plaidTokenData;
    const updatedPlaidToken = !isUpdatingPlaidLinkToken && !!plaidTokenDataUpdated;

    if (isPlaidReady && (fetchedPlaidToken || updatedPlaidToken)) {
      plaid.open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isFetchingPlaidToken,
    isPlaidReady,
    isUpdatingPlaidLinkToken,
    plaidTokenData,
    plaidTokenDataUpdated
  ]);

  const getPlaidLinkTokenHandler = useCallback(async () => {
    if (redirectToLinkedAccounts({ useTopWindow: true, intent: LINK_INTENT })) {
      return {};
    }

    return getPlaidLinkToken();
  }, [getPlaidLinkToken, redirectToLinkedAccounts]);

  const updatePlaidLinkTokenHandler = useCallback(async () => {
    if (redirectToLinkedAccounts({ useTopWindow: true })) {
      return {};
    }

    return updatePlaidLinkToken();
  }, [redirectToLinkedAccounts, updatePlaidLinkToken]);

  return {
    isPlaidReady,
    plaid,
    isHandling: isCreatingCounterPartyLoading || isRelinkAccountLoading || isLoadingToken,
    getPlaidLinkToken: getPlaidLinkTokenHandler,
    updatePlaidLinkToken: updatePlaidLinkTokenHandler,
    isUpdatingPlaidLinkToken,
    isFetchingPlaidToken
  };
}

export default usePlaid;
